/**
 * Process Artifact Manager Component
 * 
 * Manages artifacts within a process, showing their roles and allowing add/remove operations.
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Button,
  Select,
  message,
  Space,
  Tag,
  Popconfirm,
  Divider,
  Empty,
  Spin,
  Badge,
  Cascader,
  Tabs,
  Table
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  EditOutlined
} from '@ant-design/icons';

// Using items prop instead of TabPane (deprecated)
import { apiClient } from '../../services/apiClient';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { EditTransitionDialog } from './EditTransitionDialog';
import styles from './ProcessArtifactManager.module.css';

const { Option } = Select;

interface ProcessArtifact {
  association_id: string;
  artifact_id: string;
  artifact_name: string;
  artifact_description?: string;
  role: string;
  sequence_order: number;
  is_required: boolean;
  quantity: number;
  notes?: string;
  transition_id?: string;
  from_state?: {
    id: string;
    name: string;
  } | null;
  to_state?: {
    id: string;
    name: string;
  } | null;
}

interface ArtifactOption {
  id: string;
  name: string;
}

interface ArtifactState {
  id: string;
  name: string;
  description?: string | null;
  state_type: string;
}

interface ProcessSkill {
  id: string;
  process_skill_id: string;
  skill_parameter_ref: string;
  skill_name: string;
  skill_description?: string;
  skill_level?: number;
}

interface SkillOption {
  value: string;  // skill ID
  label: string;  // skill name
  description?: string;
  skill_level?: number;
  children?: SkillOption[];  // For cascader
  isLeaf?: boolean;  // For lazy loading
}

interface ProcessArtifactManagerProps {
  processId: string;
  processName?: string;
  onUpdate?: () => void;
}

const getRoleColor = (role: string): string => {
  const colors: Record<string, string> = {
    'PROCESSITEM': 'blue',
    'PROCESSITEM_PERSISTENT': 'cyan',
    'MATERIAL': 'green',
    'RESOURCE': 'orange',
    'CREATION': 'purple'
  };
  return colors[role] || 'default';
};

const getRoleDescription = (role: string): string => {
  const descriptions: Record<string, string> = {
    'PROCESSITEM': 'Primary artifact being transformed by this process',
    'PROCESSITEM_PERSISTENT': 'Primary artifact that persists after process completes',
    'MATERIAL': 'Artifact consumed during this process',
    'RESOURCE': 'Artifact used but not consumed by this process',
    'CREATION': 'Artifact created as output of this process'
  };
  return descriptions[role] || 'Unknown role';
};

export const ProcessArtifactManager: React.FC<ProcessArtifactManagerProps> = ({
  processId,
  processName,
  onUpdate
}) => {
  const [loading, setLoading] = useState(true);
  const [artifacts, setArtifacts] = useState<ProcessArtifact[]>([]);
  const [availableArtifacts, setAvailableArtifacts] = useState<ArtifactOption[]>([]);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | undefined>();
  const [selectedRole, setSelectedRole] = useState<string>('PROCESSITEM');
  const [addingArtifact, setAddingArtifact] = useState(false);
  const [activeRoleTab, setActiveRoleTab] = useState<string>('PROCESSITEM');
  const [artifactStates, setArtifactStates] = useState<ArtifactState[]>([]);
  const [selectedFromState, setSelectedFromState] = useState<string | undefined>();
  const [selectedToState, setSelectedToState] = useState<string | undefined>();
  const [associationsWithoutTransitions, setAssociationsWithoutTransitions] = useState<ProcessArtifact[]>([]);
  const [processTransitions, setProcessTransitions] = useState<any[]>([]);
  const [showTransitionDialog, setShowTransitionDialog] = useState(false);
  const [editingTransitionArtifactId, setEditingTransitionArtifactId] = useState<string | null>(null);
  const [editingTransitionRole, setEditingTransitionRole] = useState<string | null>(null);
  
  // Skills state
  const [skills, setSkills] = useState<ProcessSkill[]>([]);
  const [availableSkills, setAvailableSkills] = useState<SkillOption[]>([]);
  const [selectedSkillPath, setSelectedSkillPath] = useState<string[]>([]);  // Path in hierarchy
  const [addingSkill, setAddingSkill] = useState(false);

  useEffect(() => {
    void loadArtifacts();
    void loadAvailableArtifacts();
    void loadSkills();
    void loadAvailableSkills();
  }, [processId]);

  const loadArtifacts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/prime-processes/${processId}/artifacts`);
      setArtifacts(response.data);
      
      // Load process transitions once
      await loadProcessTransitions();
      
      // Check for associations without transitions
      await checkAssociationsWithoutTransitions(response.data);
    } catch (error: any) {
      message.error(`Failed to load artifacts: ${error.message}`);
      console.error('Error loading artifacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProcessTransitions = async () => {
    try {
      const response = await apiClient.get(`/prime-processes/${processId}/transitions`);
      setProcessTransitions(response.data || []);
    } catch (error: any) {
      // Suppress CORS/network errors - these are expected backend configuration issues
      const isCorsError = error.code === 'ERR_NETWORK' || error.message?.includes('CORS');
      if (!isCorsError) {
        console.error('Error loading process transitions:', error);
      }
      setProcessTransitions([]);
    }
  };

  const checkAssociationsWithoutTransitions = async (artifacts: ProcessArtifact[]) => {
    try {
      const associationsWithoutTransitions: ProcessArtifact[] = [];
      
      for (const artifact of artifacts) {
        // Check if this artifact has any transitions in this process using cached transitions
        const hasTransition = processTransitions.some((transition: any) => 
          transition.prime_artifact_ref === artifact.artifact_id
        );
        
        if (!hasTransition) {
          associationsWithoutTransitions.push(artifact);
        }
      }
      
      setAssociationsWithoutTransitions(associationsWithoutTransitions);
    } catch (error: any) {
      console.error('Error checking associations without transitions:', error);
    }
  };

  const hasTransitionForArtifact = (artifactId: string): boolean => {
    return processTransitions.some((transition: any) => 
      transition.prime_artifact_ref === artifactId
    );
  };

  const loadAvailableArtifacts = async () => {
    try {
      // Load all artifacts for selection
      const response = await apiClient.get('/prime-artifacts?skip=0&limit=1000');
      setAvailableArtifacts(response.data.map((a: any) => ({
        id: a.id,
        name: a.name
      })));
    } catch (error: any) {
      console.error('Error loading available artifacts:', error);
    }
  };

  const loadArtifactStates = async (artifactId: string) => {
    try {
      const states = await PrimeArtifactService.getPrimeArtifactStates(artifactId);
      setArtifactStates(states);
      // Reset state selections
      setSelectedFromState(undefined);
      setSelectedToState(undefined);
    } catch (error: any) {
      console.error('Error loading artifact states:', error);
      setArtifactStates([]);
    }
  };

  // Skills Management Functions
  const loadSkills = async () => {
    try {
      const response = await apiClient.get(`/prime-processes/${processId}/skills`);
      setSkills(response.data || []);
    } catch (error: any) {
      console.error('Error loading skills:', error);
      setSkills([]);
    }
  };

  const loadAvailableSkills = async () => {
    try {
      const response = await apiClient.get('/skills/tree');
      const skillTree = response.data.roots || [];
      
      // Convert tree structure to cascader format
      const convertToCascaderOptions = (nodes: any[]): SkillOption[] => {
        return nodes.map((node: any) => ({
          value: node.id,
          label: node.name,
          description: node.description,
          skill_level: node.skill_level,
          children: node.children && node.children.length > 0 
            ? convertToCascaderOptions(node.children) 
            : undefined,
          isLeaf: !node.children || node.children.length === 0
        }));
      };
      
      setAvailableSkills(convertToCascaderOptions(skillTree));
    } catch (error: any) {
      console.error('Error loading available skills:', error);
      setAvailableSkills([]);
    }
  };

  const handleAddSkill = async () => {
    if (!selectedSkillPath || selectedSkillPath.length === 0) {
      message.warning('Please select a skill');
      return;
    }

    // The last item in the path is the selected skill ID
    const selectedSkillId = selectedSkillPath[selectedSkillPath.length - 1];

    try {
      setAddingSkill(true);
      await apiClient.post(`/prime-processes/${processId}/skills`, {
        skill_parameter_ref: selectedSkillId
      });
      
      message.success('Skill added to process');
      setSelectedSkillPath([]);
      await loadSkills();
      if (onUpdate) onUpdate();
    } catch (error: any) {
      console.error('Error adding skill:', error);
      let errorMsg = 'Failed to add skill';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle both string and object/array detail formats
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      message.error(errorMsg);
    } finally {
      setAddingSkill(false);
    }
  };

  const handleRemoveSkill = async (processSkillId: string) => {
    try {
      await apiClient.delete(`/prime-processes/${processId}/skills/${processSkillId}`);
      message.success('Skill removed from process');
      await loadSkills();
      if (onUpdate) onUpdate();
    } catch (error: any) {
      message.error(`Failed to remove skill: ${error.message}`);
    }
  };

  const handleAddArtifact = () => {
    if (!selectedArtifactId) {
      message.warning('Please select an artifact');
      return;
    }

    // Open the transition dialog for creating a new transition
    setEditingTransitionArtifactId(selectedArtifactId);
    setEditingTransitionRole(selectedRole);
    setShowTransitionDialog(true);
  };

  const handleAddArtifactDialogSave = async () => {
    // After saving, reset the add artifact form
    setShowTransitionDialog(false);
    setEditingTransitionArtifactId(null);
    setEditingTransitionRole(null);
    setSelectedArtifactId(undefined);
    
    // Reload data
    await loadProcessTransitions();
    void loadArtifacts();
    if (onUpdate) onUpdate();
    
    message.success('Transition created and artifact added to process');
  };

  const handleCreateTransitionForAssociation = (artifact: ProcessArtifact) => {
    // Open the transition dialog for this artifact
    setEditingTransitionArtifactId(artifact.artifact_id);
    setEditingTransitionRole(artifact.role);
    setShowTransitionDialog(true);
  };

  const handleTransitionDialogClose = () => {
    setShowTransitionDialog(false);
    setEditingTransitionArtifactId(null);
    setEditingTransitionRole(null);
    // Don't reset selectedArtifactId here - only reset on save
  };

  const handleTransitionDialogSave = async () => {
    setShowTransitionDialog(false);
    setEditingTransitionArtifactId(null);
    setEditingTransitionRole(null);
    
    // Reload data to show the new transition
    await loadProcessTransitions();
    void loadArtifacts();
    if (onUpdate) onUpdate();
  };

  const handleRemoveArtifact = async (artifactId: string, role: string) => {
    try {
      await apiClient.delete(`/prime-processes/${processId}/artifacts/${artifactId}`, {
        params: { role }
      });

      message.success('Artifact removed from process');
      void loadArtifacts();
      if (onUpdate) onUpdate();
    } catch (error: any) {
      message.error(`Failed to remove artifact: ${error.response?.data?.detail || error.message}`);
      console.error('Error removing artifact:', error);
    }
  };

  const artifactsByRole = artifacts.reduce((acc, artifact) => {
    if (!acc[artifact.role]) {
      acc[artifact.role] = [];
    }
    acc[artifact.role].push(artifact);
    return acc;
  }, {} as Record<string, ProcessArtifact[]>);

  const renderArtifactItem = (artifact: ProcessArtifact) => {
    // Check if this artifact has a transition using cached data
    const hasTransition = hasTransitionForArtifact(artifact.artifact_id);

    const actions = [];
    
    // Add transition creation button if no transition exists
    if (!hasTransition) {
      actions.push(
        <Button
          key="create-transition"
          type="primary"
          size="small"
          onClick={() => handleCreateTransitionForAssociation(artifact)}
          loading={addingArtifact}
        >
          Create Transition
        </Button>
      );
    }
    
    // Add remove button
    actions.push(
      <Popconfirm
        key="remove"
        title="Remove this artifact from the process?"
        onConfirm={() => handleRemoveArtifact(artifact.artifact_id, artifact.role)}
        okText="Yes"
        cancelText="No"
      >
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          size="small"
        />
      </Popconfirm>
    );

    return (
      <List.Item
        actions={actions}
        style={{ 
          backgroundColor: !hasTransition ? '#fff7e6' : 'transparent',
          border: !hasTransition ? '1px solid #ffa940' : 'none',
          borderRadius: !hasTransition ? '4px' : '0',
          padding: !hasTransition ? '8px' : '0'
        }}
      >
        <List.Item.Meta
          title={
            <Space>
              <strong>{artifact.artifact_name}</strong>
              {artifact.is_required && <Tag color="red">Required</Tag>}
              {artifact.quantity > 1 && <Tag>Qty: {artifact.quantity}</Tag>}
              {!hasTransition && <Tag color="orange">⚠️ No Transition</Tag>}
            </Space>
          }
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {artifact.artifact_description && (
                <div>{artifact.artifact_description}</div>
              )}
              {artifact.notes && (
                <div style={{ fontStyle: 'italic', color: '#666' }}>{artifact.notes}</div>
              )}
              {!hasTransition && (
                <div style={{ fontSize: '12px', color: '#d46b08' }}>
                  ⚠️ This artifact is associated with the process but doesn't have a transition yet.
                </div>
              )}
              {(artifact.from_state || artifact.to_state) && (
                <div style={{ marginTop: 4 }}>
                  <Space size="small">
                    {artifact.from_state && (
                      <Tag color="blue" icon={<span style={{ marginRight: 4 }}>FROM:</span>}>
                        {artifact.from_state.name}
                      </Tag>
                    )}
                    {artifact.from_state && artifact.to_state && (
                      <span style={{ color: '#999' }}>→</span>
                    )}
                    {artifact.to_state && (
                      <Tag color="green" icon={<span style={{ marginRight: 4 }}>TO:</span>}>
                        {artifact.to_state.name}
                      </Tag>
                    )}
                  </Space>
                </div>
              )}
            </Space>
          }
        />
      </List.Item>
    );
  };

  const renderArtifactList = (role: string, artifacts: ProcessArtifact[]) => (
    <Card
      key={role}
      size="small"
      title={
        <Space>
          <Tag color={getRoleColor(role)}>{role}</Tag>
          <Badge count={artifacts.length} />
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {artifacts.length > 0 ? (
        <List
          dataSource={artifacts}
          renderItem={renderArtifactItem}
        />
      ) : (
        <Empty
          description={`No ${role} artifacts`}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Card>
  );

  const renderSkillsSection = () => (
    <Card
      key="skills"
      size="small"
      title={
        <Space>
          <Tag color="gold">SKILLS</Tag>
          <Badge count={skills.length} />
        </Space>
      }
      style={{ marginBottom: 16, marginLeft: 24 }}
    >
      {/* Add Skill Section */}
      <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fafafa', borderRadius: 4 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Add Skill:</label>
          <Space style={{ width: '100%' }}>
            <Cascader
              style={{ flex: 1, minWidth: 350 }}
              options={availableSkills}
              value={selectedSkillPath}
              onChange={(value) => setSelectedSkillPath(value as string[])}
              placeholder="Select skill category > subcategory > skill..."
              showSearch={{
                filter: (inputValue, path) => {
                  return path.some(option => 
                    option.label?.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }
              }}
              expandTrigger="hover"
              changeOnSelect={false}
              displayRender={(labels, selectedOptions) => {
                return (
                  <>
                    {labels.map((label, index) => {
                      const option = selectedOptions?.[index];
                      const levelPrefix = option?.skill_level !== undefined ? `L${option.skill_level}:` : '';
                      return (
                        <span key={index}>
                          {index > 0 && ' > '}
                          <span style={{ fontSize: '11px', color: '#888', marginRight: 4 }}>{levelPrefix}</span>
                          {label}
                        </span>
                      );
                    })}
                  </>
                );
              }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddSkill}
              disabled={selectedSkillPath.length === 0}
              loading={addingSkill}
              size="small"
            >
              Add
            </Button>
          </Space>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <InfoCircleOutlined /> Navigate through skill categories to select the most specific skill needed
          </div>
        </Space>
      </div>

      {/* Skills List */}
      {skills.length > 0 ? (
        <List
          dataSource={skills}
          renderItem={(skill) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="remove"
                  title="Remove this skill from the process?"
                  onConfirm={() => handleRemoveSkill(skill.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                  />
                </Popconfirm>
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <strong>{skill.skill_name}</strong>
                    {skill.skill_level !== undefined && (
                      <Tag color="blue">Level {skill.skill_level}</Tag>
                    )}
                  </Space>
                }
                description={skill.skill_description}
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty
          description="No skills assigned to this process"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Card>
  );

  // Compact Artifact Table Component
  const CompactArtifactTable = ({ artifacts: roleArtifacts }: { artifacts: ProcessArtifact[] }) => (
    <Table
      dataSource={roleArtifacts}
      rowKey={(record) => record.artifact_id || `artifact-${record.artifact_name}-${record.from_state?.id || 'no-from'}-${record.to_state?.id || 'no-to'}`}
      size="small"
      pagination={false}
      scroll={{ y: 400 }}
      locale={{ emptyText: <Empty description="No artifacts in this role" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      columns={[
        { 
          title: 'Artifact', 
          dataIndex: 'artifact_name', 
          key: 'name', 
          width: '35%',
          ellipsis: true
        },
        { 
          title: 'States', 
          key: 'states',
          width: '30%',
          render: (_, record) => (
            <Space size="small">
              {record.from_state && <Tag color="blue" style={{ fontSize: 11 }}>{record.from_state.name}</Tag>}
              {record.from_state && record.to_state && <span style={{ color: '#999' }}>→</span>}
              {record.to_state && <Tag color="green" style={{ fontSize: 11 }}>{record.to_state.name}</Tag>}
            </Space>
          )
        },
        { 
          title: 'Transition',
          key: 'transition',
          width: '15%',
          align: 'center' as const,
          render: (_, record) => (
            record.transition_id ? <span style={{ color: '#52c41a' }}>✓</span> : <Tag color="orange">None</Tag>
          )
        },
        {
          title: 'Actions',
          key: 'actions',
          width: '20%',
          align: 'center' as const,
          render: (_, record) => (
            <Space size="small">
              <Button 
                size="small" 
                icon={<EditOutlined />} 
                onClick={() => handleCreateTransitionForAssociation(record)}
                title="Edit transition"
              />
              <Popconfirm 
                title="Remove this artifact from the process?" 
                onConfirm={() => handleRemoveArtifact(record.artifact_id, record.role)}
                okText="Yes"
                cancelText="No"
              >
                <Button size="small" danger icon={<DeleteOutlined />} title="Remove artifact" />
              </Popconfirm>
            </Space>
          )
        }
      ]}
    />
  );

  // Compact Skills Table Component
  const CompactSkillsTable = () => (
    <div>
      {/* Add Skill Section */}
      <div className="compact-add-artifact" style={{ marginBottom: 16, padding: 12, backgroundColor: '#fafafa', borderRadius: 4 }}>
        <Space style={{ width: '100%' }}>
          <Cascader
            style={{ flex: 1, minWidth: 300 }}
            options={availableSkills}
            value={selectedSkillPath}
            onChange={(value) => setSelectedSkillPath(value as string[])}
            placeholder="Select skill..."
            showSearch={{
              filter: (inputValue, path) => {
                return path.some(option => 
                  option.label?.toLowerCase().includes(inputValue.toLowerCase())
                );
              }
            }}
            expandTrigger="hover"
            changeOnSelect={false}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddSkill}
            disabled={selectedSkillPath.length === 0}
            loading={addingSkill}
            size="small"
          >
            Add
          </Button>
        </Space>
      </div>

      {/* Skills List Table */}
      <Table
        dataSource={skills}
        size="small"
        pagination={false}
        scroll={{ y: 350 }}
        locale={{ emptyText: <Empty description="No skills assigned" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        columns={[
          { 
            title: 'Skill', 
            dataIndex: 'skill_name', 
            key: 'name', 
            width: '60%',
            ellipsis: true
          },
          { 
            title: 'Level', 
            dataIndex: 'skill_level', 
            key: 'level', 
            width: '20%',
            render: (level) => level !== undefined ? `L${level}` : '-'
          },
          {
            title: 'Actions',
            key: 'actions',
            width: '20%',
            align: 'center' as const,
            render: (_, record) => (
              <Popconfirm
                title="Remove this skill?"
                onConfirm={() => handleRemoveSkill(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )
          }
        ]}
      />
    </div>
  );

  return (
    <div className={styles.processArtifactManager}>
      {/* Transition Dialog */}
      {editingTransitionArtifactId && (
        <EditTransitionDialog
          visible={showTransitionDialog}
          transitionId={null}
          artifactId={editingTransitionArtifactId}
          processId={processId}
          defaultRole={editingTransitionRole || undefined}
          onClose={handleTransitionDialogClose}
          onSave={selectedArtifactId ? handleAddArtifactDialogSave : handleTransitionDialogSave}
        />
      )}

      <Card
        title={`Manage Artifacts: ${processName || processId}`}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
      >
        {/* Main Layout: Tabs + Add Section Side by Side */}
        <div className={styles.artifactManagerLayout}>
          {/* Role-Based Tabs - Takes most of the width */}
          <div className={styles.tabsSection}>
            <Spin spinning={loading} style={{ height: '100%' }}>
              <Tabs 
                activeKey={activeRoleTab} 
                onChange={setActiveRoleTab}
                style={{ height: '100%' }}
                tabBarStyle={{ marginBottom: 4 }}
                items={[
                  {
                    key: 'PROCESSITEM',
                    label: `PROCESSITEM (${(artifactsByRole['PROCESSITEM'] || []).length})`,
                    children: <CompactArtifactTable artifacts={artifactsByRole['PROCESSITEM'] || []} />
                  },
                  {
                    key: 'PROCESSITEM_PERSISTENT',
                    label: `PROCESSITEM_PERSISTENT (${(artifactsByRole['PROCESSITEM_PERSISTENT'] || []).length})`,
                    children: <CompactArtifactTable artifacts={artifactsByRole['PROCESSITEM_PERSISTENT'] || []} />
                  },
                  {
                    key: 'MATERIAL',
                    label: `MATERIAL (${(artifactsByRole['MATERIAL'] || []).length})`,
                    children: <CompactArtifactTable artifacts={artifactsByRole['MATERIAL'] || []} />
                  },
                  {
                    key: 'RESOURCE',
                    label: `RESOURCE (${(artifactsByRole['RESOURCE'] || []).length})`,
                    children: <CompactArtifactTable artifacts={artifactsByRole['RESOURCE'] || []} />
                  },
                  {
                    key: 'CREATION',
                    label: `CREATION (${(artifactsByRole['CREATION'] || []).length})`,
                    children: <CompactArtifactTable artifacts={artifactsByRole['CREATION'] || []} />
                  },
                  {
                    key: 'SKILLS',
                    label: `SKILLS (${skills.length})`,
                    children: <CompactSkillsTable />
                  }
                ]}
              />
            </Spin>
          </div>

          {/* Add Artifact Section - Sidebar on the right */}
          <div className={styles.addArtifactSidebar}>
            <Card 
              size="small" 
              title="Add Artifact" 
              style={{ height: '100%' }}
              styles={{ body: { display: 'flex', flexDirection: 'column', gap: 12 } }}
            >
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12 }}>
                  Artifact:
                </label>
                <Select
                  placeholder="Select..."
                  style={{ width: '100%' }}
                  value={selectedArtifactId}
                  onChange={(value) => {
                    setSelectedArtifactId(value);
                    if (value) {
                      void loadArtifactStates(value);
                    } else {
                      setArtifactStates([]);
                    }
                  }}
                  showSearch
                  size="small"
                  filterOption={(input, option: any) => {
                    if (option?.children) {
                      return String(option.children).toLowerCase().includes(input.toLowerCase());
                    }
                    return false;
                  }}
                >
                  {availableArtifacts.map((artifact) => (
                    <Option key={artifact.id} value={artifact.id}>
                      {artifact.name}
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12 }}>
                  Role:
                </label>
                <Select
                  value={selectedRole}
                  onChange={setSelectedRole}
                  style={{ width: '100%' }}
                  size="small"
                >
                  <Option value="PROCESSITEM">PROCESSITEM</Option>
                  <Option value="PROCESSITEM_PERSISTENT">PROCESSITEM_PERSISTENT</Option>
                  <Option value="MATERIAL">MATERIAL</Option>
                  <Option value="RESOURCE">RESOURCE</Option>
                  <Option value="CREATION">CREATION</Option>
                </Select>
              </div>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddArtifact}
                disabled={!selectedArtifactId}
                loading={addingArtifact}
                block
                size="small"
              >
                Add to Process
              </Button>

              <div style={{ fontSize: 11, color: '#666', marginTop: 'auto', paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                {getRoleDescription(selectedRole)}
              </div>
            </Card>
          </div>
        </div>
      </Card>
    </div>
  );
};

